import React, {
  useEffect, useMemo, useState,
} from 'react';

import {
  Alert,
  Box,
  Stack, Typography,
} from '@mui/material';
import dayjs from 'dayjs';

import Autocomplete from '../../../../components/Autocomplete';
import Button from '../../../../components/Button';
import Chip from '../../../../components/Chip';
import ContentCard from '../../../../components/ContentCard';
import DataGrid from '../../../../components/DataGrid';
import { DateTimePicker } from '../../../../components/DatePicker';
import Modal from '../../../../components/Modal';
import SearchField from '../../../../components/SearchField';
import StudentCard from '../../../../components/StudentCard';
import StudentListItem from '../../../../components/StudentListItem';
import Tabs from '../../../../components/Tabs';
import TextField from '../../../../components/TextField';
import { useEnrolToClassMutation, useUnenrolFromClassMutation } from '../../../../services/classes';
import { Class, Lesson } from '../../../../services/classes/types';
import { useGetStudentsQuery } from '../../../../services/students';
import { ShortStudent } from '../../../../services/students/types';
import { UnenrolmentType } from '../../../../types/common';
import {
  DEFAULT_UNENROLMENT_REASON,
  ENROLLABLE_STUDENT_STATES,
  MAX_CLASS_SIZE,
  UNENROLMENT_MOVE_REASONS,
  UNENROLMENT_QUIT_REASONS,
  UNENROLMENT_TYPES,
} from '../../../../utils/common/constants';
import useError from '../../../../utils/react/hooks/useError';
import useSuccess from '../../../../utils/react/hooks/useSuccess';
import ClassLessonCard from '../../ClassLessonCard';
import { StudentsHeadingWrapper } from '../../index.styles';

interface StudentsCardProps {
  classObj: Class;
  lessons?: Lesson[];
}

const StudentsCard = ({ classObj, lessons }: StudentsCardProps) => {
  const [searchStr, setSearchStr] = useState('');
  const [enrolFormOpen, setEnrolFormOpen] = useState(false);
  const [enrolDate, setEnrolDate] = useState(dayjs());

  const [unenrolFormOpen, setUnenrolFormOpen] = useState(false);
  const [unenrolDate, setUnenrolDate] = useState(dayjs());

  // Unenrol reasons
  const [selectedUnenrolType, setSelectedUnenrolType] = useState(UnenrolmentType.Quit);
  const [selectedUnenrolReasonCategory, setSelectedUnenrolReasonCategory] = useState<{
    value: string;
    label: string;
  } | undefined>(DEFAULT_UNENROLMENT_REASON);
  const [unenrolReason, setUnenrolReason] = useState('');

  const { data: searchedStudents, isFetching: isSearchedStudentsLoading } = useGetStudentsQuery({
    search: searchStr,
  }, { skip: searchStr === '' || !enrolFormOpen });

  const [enrolStudent, {
    isLoading: isEnrolStudentLoading,
    isError: isEnrolStudentError,
    error: enrolStudentError,
    isSuccess: isEnrolStudentSuccess,
  }] = useEnrolToClassMutation();

  const [unenrolStudent, {
    isLoading: isUnenrolStudentLoading,
    isError: isUnenrolStudentError,
    error: unenrolStudentError,
    isSuccess: isUnenrolStudentSuccess,
  }] = useUnenrolFromClassMutation();

  const [selectedStudent, setSelectedStudent] = useState<ShortStudent>();

  const handleEnrolClose = () => {
    setEnrolFormOpen(false);
    setSelectedStudent(undefined);
    setEnrolDate(dayjs());
  };

  const handleUnenrolClose = () => {
    setUnenrolFormOpen(false);
    setSelectedStudent(undefined);
    setUnenrolDate(dayjs());
    setSelectedUnenrolType(UnenrolmentType.Quit);
    setSelectedUnenrolReasonCategory(DEFAULT_UNENROLMENT_REASON);
    setUnenrolReason('');
  };

  useEffect(() => {
    if (isEnrolStudentSuccess) handleEnrolClose();
  }, [isEnrolStudentSuccess]);

  useEffect(() => {
    if (isUnenrolStudentSuccess) handleUnenrolClose();
  }, [isUnenrolStudentSuccess]);

  const firstEnrolLesson = useMemo(() =>
    lessons?.find((l) => l.startDatetime > enrolDate.valueOf()), [enrolDate, lessons]);
  const firstUnenrolLesson = useMemo(() =>
    lessons?.find((l) => l.startDatetime > unenrolDate.valueOf()), [unenrolDate, lessons]);

  useError(isEnrolStudentError, enrolStudentError);
  useSuccess(isEnrolStudentSuccess, 'Student enrolled');

  useError(isUnenrolStudentError, unenrolStudentError);
  useSuccess(isUnenrolStudentSuccess, 'Student unenrolled');

  return (
    <ContentCard
      variant="outlined"
      title={(
        <StudentsHeadingWrapper>
          <Typography variant="h6">Students</Typography>
          <Chip
            label={`${classObj?.studentCount} / ${MAX_CLASS_SIZE}`}
            variant="outlined"
            size="small"
          />
        </StudentsHeadingWrapper>
      )}
      buttons={[
        {
          label: 'Enrol Student',
          onClick: () => setEnrolFormOpen(true),
          disabled: !classObj.active,
        },
      ]}
    >
      <DataGrid
        columns={[
          {
            field: 'name',
            headerName: 'Name',
            width: 200,
          },
          {
            field: 'school',
            headerName: 'School',
            width: 200,
          },
          // MoreIcon on each row with option to unenrol
          {
            field: 'unenrol',
            headerName: 'Unenrol',
            width: 100,
            renderCell: (params) => (
              <Button
                color="error"
                size="small"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setSelectedStudent(params.row as ShortStudent);
                  setUnenrolFormOpen(true);
                }}
                allCaps
                variant="text"
              >
                Unenrol
              </Button>
            ),
          },
        ]}
        getRowHref={(row) => `/admin/student/${row.studentId}`}
        getRowId={(row) => row.studentId}
        rows={classObj?.students ?? []}
      />
      <Modal
        open={enrolFormOpen}
        handleClose={handleEnrolClose}
        title="Enrol Student"
        scrollY
        buttons={
          [
            {
              label: 'Enrol',
              onClick: () => enrolStudent({
                subject: classObj.subject,
                classId: classObj.classId,
                studentId: selectedStudent?.studentId ?? '',
                enrolFromDatetime: enrolDate.valueOf(),
              }),
              color: 'darkPrimary',
              loading: isEnrolStudentLoading,
              disabled: !selectedStudent,
            },
          ]
        }
      >
        <Stack direction="column" spacing={2}>
          <SearchField
            type="dropdown"
            placeholder="Search students..."
            options={searchedStudents?.data.filter(
              (s) => ENROLLABLE_STUDENT_STATES.includes(s.state) && !classObj.students.find(
                (cs) => cs.studentId === s.studentId,
              ),
            )
           || []}
            getOptionKey={(s) => s.studentId}
            getOptionLabel={(s) => s.name}
            onSearch={(search) => setSearchStr(search)}
            loading={isSearchedStudentsLoading}
            onSelect={(s) => setSelectedStudent(s)}
            renderOption={(props, s) => (
              <StudentListItem
                student={s}
                {...props}
                key={s.id}
              />
            )}
            clearOnSelect
          />
          {selectedStudent ? (
            <ContentCard variant="outlined">
              <StudentCard
                student={selectedStudent}
                onClose={() => setSelectedStudent(undefined)}
                showState={false}
              />
            </ContentCard>
          ) : (
            <ContentCard variant="outlined">
              <Box padding={1}>
                <Typography variant="overline">
                  No student selected
                </Typography>
              </Box>
            </ContentCard>
          )}
          <Stack direction="column" spacing={2} alignItems="flex-start">
            <Typography>
              Enrol from:
            </Typography>
            <DateTimePicker
              value={enrolDate}
              onChange={(newDate) => {
                if (newDate) setEnrolDate(newDate);
              }}
            />
          </Stack>
          <Alert severity="info">
            This student will be enrolled into all lessons from the following lesson onwards.
          </Alert>
          {firstEnrolLesson ? (
            <ClassLessonCard
              lesson={firstEnrolLesson}
            />
          ) : (
            <ContentCard>
              <Box padding={2}>
                <Typography variant="overline">
                  No lessons after this date
                </Typography>
              </Box>
            </ContentCard>
          )}
        </Stack>
      </Modal>
      <Modal
        open={unenrolFormOpen}
        handleClose={handleUnenrolClose}
        title={`Unenrol ${selectedStudent?.name}`}
        scrollY
        buttons={
          [
            {
              label: 'Unenrol',
              onClick: () => unenrolStudent({
                subject: classObj.subject,
                classId: classObj.classId,
                studentId: selectedStudent?.studentId ?? '',
                unenrolFromDatetime: unenrolDate.valueOf(),
                reasonObj: {
                  reasonCategory: selectedUnenrolReasonCategory?.value ?? '',
                  reasonType: selectedUnenrolType,
                  reason: unenrolReason,
                },
              }),
              color: 'error',
              loading: isUnenrolStudentLoading,
              disabled: !selectedStudent,
            },
          ]
        }
      >
        <Stack direction="column" spacing={2} alignItems="flex-start">
          <Typography>
            Unenrol from:
          </Typography>
          <DateTimePicker
            value={enrolDate}
            onChange={(newDate) => {
              if (newDate) setUnenrolDate(newDate);
            }}
          />
          <Stack direction="column" spacing={2}>
            <Alert severity="warning">
              This student will be unenrolled from all lessons from the following lesson onwards.
            </Alert>
            {firstUnenrolLesson ? (
              <ClassLessonCard
                lesson={firstUnenrolLesson}
              />
            )
              : (
                <ContentCard>
                  <Box padding={2}>
                    <Typography variant="overline">
                      No lessons after this date
                    </Typography>
                  </Box>
                </ContentCard>
              )}
            <ContentCard variant="outlined">
              <Stack
                direction="column"
                spacing={2}
              >
                <Typography>Unenrol type:</Typography>
                <Tabs
                  options={UNENROLMENT_TYPES}
                  justifyContent="flex-start"
                  selectedKey={selectedUnenrolType}
                  onChange={(value) => {
                    setSelectedUnenrolType(value as UnenrolmentType);
                    setSelectedUnenrolReasonCategory(DEFAULT_UNENROLMENT_REASON);
                  }}
                />
                <Typography>Unenrol reason:</Typography>
                <Autocomplete
                  // TODO The fields here seem to autounfocus for some reason need to fix
                  options={selectedUnenrolType === UnenrolmentType.Quit ?
                    UNENROLMENT_QUIT_REASONS
                    : UNENROLMENT_MOVE_REASONS}
                  getOptionLabel={(option) => option.label}
                  value={selectedUnenrolReasonCategory}
                  onChange={(event: any, newValue: any) => {
                    setSelectedUnenrolReasonCategory(newValue);
                  }}
                />
                <TextField
                  placeholder="Unenrol notes"
                  multiline
                  fullWidth
                  value={unenrolReason}
                  onChange={(event) => setUnenrolReason(event.target.value)}
                />
              </Stack>
            </ContentCard>
          </Stack>
        </Stack>
      </Modal>
    </ContentCard>
  );
};
export default StudentsCard;
